// src/pages/AddStockTransferNote.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import "../css/vehicle.css";
import useAuthRedirect from "../hooks/useAuthRedirect";
import InventoryNav from "../components/inventoryNav";
import SupplierListModal from "../components/stnOutwardModal"; 
import Time from "../components/time";
import Swal from "sweetalert2";
import { useNavigate, Link } from "react-router-dom";
import { hasPermission } from "../utils/permissionHelper";
import Access from "../components/access";

const AddStockTransferNote = () => {
  useAuthRedirect();

  const navigate = useNavigate();
  const branch = localStorage.getItem("branch");
  const userId = localStorage.getItem("username");

  const canEntry = hasPermission("STN Outward Pending", "entry");

  const [showReqModal, setShowReqModal] = useState(false);

  const [selectedItems, setSelectedItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);

  const [stnNo, setStnNo] = useState("");
  const [branches, setBranches] = useState([]);

  const [formData, setFormData] = useState({
    newPurNo: "",
    stnReqNo: "",
    branches: "", // toBranch
    remarks: "",
  });

  // ---------- Normalizer ----------
  const normalizeItem = (raw) => {
    const itemCode =
      raw.stn_itemcode ?? raw.itemmaster_code ?? raw.itemCode ?? "";
    const itemName =
      raw.stn_desc ?? raw.itemmaster_name ?? raw.itemName ?? "";
    const uom = raw.stn_uom ?? raw.itemmaster_uom ?? raw.uom ?? "";
    const price = Number(raw.itemmaster_price ?? raw.stn_price ?? raw.price ?? 0) || 0;
    const qty = Number(raw.stn_qty ?? raw.qty ?? 0) || 0;
    const stock =
      Number(
        raw.itemmaster_closingStock ??
          raw.closingStock ??
          raw.stock ??
          0
      ) || 0;
    const drawNo = raw.stn_drawno ?? raw.drawNo ?? "";

    return {
      stn_itemcode: itemCode,
      stn_desc: itemName,
      stn_uom: uom,
      itemmaster_price: price,
      stn_qty: qty,
      itemmaster_closingStock: stock,
      stn_drawno: drawNo,
    };
  };

  // ---------- Initial load (STN number + branch list) ----------
  useEffect(() => {
    if (!branch || !canEntry) return;

    let progress = 0;
    let interval;

    Swal.fire({
      title: "Loading STN Setup",
      html: `
        <div id="progress-container" style="width: 100%; background: #e0e0e0; border-radius: 5px;">
          <div id="progress-bar" style="height: 10px; width: 0%; background: linear-gradient(to right, #4facfe, #00f2fe); border-radius: 5px;"></div>
        </div>
        <p id="progress-text" style="margin-top: 10px; font-size: 14px;">Loading... 0%</p>
      `,
      showConfirmButton: false,
      allowOutsideClick: false,
      didOpen: () => {
        const bar = document.getElementById("progress-bar");
        const text = document.getElementById("progress-text");
        interval = setInterval(() => {
          progress += 5;
          if (progress > 95) progress = 95;
          if (bar) bar.style.width = `${progress}%`;
          if (text) text.innerText = `Loading... ${progress}%`;
        }, 100);
      },
    });

    (async () => {
      try {
        const [branchRes, stnRes] = await Promise.all([
          axios.get(`https://rfs2.infraconsoft.net.in/api/stn_branch`),
          axios.get(`https://rfs2.infraconsoft.net.in/api/stnOutward_no?branch=${branch}`),
        ]);

        setBranches(branchRes.data.branch || []);

        if (stnRes.data?.stnNumber) {
          const purchaseNo = parseInt(stnRes.data.stnNumber, 10) || 0;
          setStnNo(String(purchaseNo));
          setFormData((prev) => ({ ...prev, newPurNo: String(purchaseNo) }));
        }

        const bar = document.getElementById("progress-bar");
        const text = document.getElementById("progress-text");
        if (bar) bar.style.width = "100%";
        if (text) text.innerText = "Loading... 100%";
        setTimeout(() => Swal.close(), 400);
      } catch (error) {
        console.error("Error during initial STN fetch:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load branch list or STN number.",
        });
      } finally {
        if (interval) clearInterval(interval);
      }
    })();
  }, [branch, canEntry]);

  // ---------- Load items for a chosen STN number ----------
  const loadItemsByReqNo = async (stnReqNo, toBranch) => {
    setLoadingItems(true);
    try {
      const res = await axios.get("https://rfs2.infraconsoft.net.in/api/stnOutward_itemsByNumber", {
        params: { branchcode: branch, toBranch, stnReqNo },
      });
      const rows = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setSelectedItems(rows.map(normalizeItem));
    } catch (err) {
      console.error("Error loading items:", err);
      setSelectedItems([]);
      Swal.fire("Error", "Failed to load items for the selected STN.", "error");
    }
    setLoadingItems(false);
  };

  // ---------- STN Req selection callback from modal ----------
  const handleVehicleSelect = ({ stnReqNo, toBranch }) => {
    setFormData((prev) => ({
      ...prev,
      stnReqNo,
      branches: toBranch,
    }));
    setSelectedItems([]); // reset old items
    loadItemsByReqNo(stnReqNo, toBranch); // <-- fetch all items now
  };

  // ---------- Form handlers ----------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ---------- Submit ----------
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.stnReqNo || !formData.branches) {
      Swal.fire("Error", "Please select an STN Request first.", "error");
      return;
    }

    if (selectedItems.length === 0) {
      Swal.fire("Error", "No items found for this requisition.", "error");
      return;
    }

    // validations
    for (let item of selectedItems) {
      const stock = Number(item.itemmaster_closingStock) || 0;
      const qty = Number(item.stn_qty) || 0;
      const name = item.stn_desc || item.stn_itemcode;

      if (qty > stock) {
        Swal.fire(
          "Error",
          `Quantity for "${name}" (${qty}) cannot exceed available stock (${stock}).`,
          "error"
        );
        return;
      }
      if (qty <= 0) {
        Swal.fire("Error", `Quantity for "${name}" must be greater than 0.`, "error");
        return;
      }
    }

    const payload = {
      stnReqNo: formData.stnReqNo,
      branch: branch,               // from-branch
      stnNo: formData.newPurNo,     // outward number
      user: userId,
      toBranch: formData.branches,  // to-branch
      remarks: formData.remarks,
      items: selectedItems.map((item) => ({
        itemCode: item.stn_itemcode,
        itemName: item.stn_desc,
        uom: item.stn_uom,
        price: Number(item.itemmaster_price) || 0,
        qty: Number(item.stn_qty) || 0,
      })),
    };

    try {
      Swal.fire({
        title: "Submitting...",
        text: "Please wait while we process your request.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      await axios.post("https://rfs2.infraconsoft.net.in/api/stnOutwardNew_insert", payload);

      Swal.fire("Success", "STN Outwarded successfully", "success");
      setSelectedItems([]);
      navigate("/inventory/stockTransferNoteView_new");
    } catch (error) {
      console.error("Error submitting form:", error);
      Swal.fire("Error", "Submission failed. Please try again.", "error");
    }
  };

  if (!canEntry) {
    return (
      <div id="admin-container">
        <InventoryNav />
        <div className="content-right" style={{ padding: 20 }}>
          <Access />
        </div>
      </div>
    );
  }

  return (
    <div id="admin-container">
      <InventoryNav />
      <div className="content-right">
        <section>
          <div className="back-div">
            <h2 className="add-txt">STOCK TRANSFER NOTE - Add</h2>
            <Link to={`/inventory/stockTransferNoteView_new`}>
              <p className="back-button">
                <i className="fa-solid fa-backward"></i>Back
              </p>
            </Link>
          </div>
          <Time />
        </section>

        <div className="form-container">
          <form className="vehicle-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Stock Transfer No :</label>
                <input
                  type="text"
                  name="newPurNo"
                  value={formData.newPurNo}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Date :</label>
                <input
                  type="date"
                  name="endhrs"
                  value={new Date().toISOString().split("T")[0]}
                  readOnly
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>To Branch / STN Req :</label>
                <input
                  type="text"
                  name="branches"
                  value={formData.branches || ""}
                  readOnly
                  onClick={() => setShowReqModal(true)}
                  placeholder="Select STN Req"
                />
              </div>
              <div className="form-group">
                <label>Transfer Type :</label>
                <input
                  type="text"
                  name="hod"
                  value="Outward"
                  readOnly
                  onChange={() => {}}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Remarks :</label>
                <input
                  type="text"
                  name="remarks"
                  value={formData.remarks || ""}
                  onChange={handleChange}
                  placeholder="remarks"
                />
              </div>
              <div className="form-group">
                <label>STN Req No :</label>
                <input
                  type="text"
                  name="stnReqNo"
                  value={formData.stnReqNo || ""}
                  readOnly
                  onClick={() => setShowReqModal(true)}
                  placeholder="Select STN Req"
                />
              </div>
            </div>

            <div className="content-table">
              <h3>Items for {formData.stnReqNo || "—"}</h3><br/>
              <table>
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Item Code</th>
                    <th>Item Name</th>
                    <th>UOM</th>
                    <th>Stock</th>
                    <th>Price</th>
                    <th>Qty</th>
                    <th>Value</th>
                    <th>Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingItems ? (
                    <tr>
                      <td colSpan="10" className="loading-text">Loading items...</td>
                    </tr>
                  ) : selectedItems.length > 0 ? (
                    selectedItems.map((item, index) => {
                      const price = Number(item.itemmaster_price) || 0;
                      const qty = Number(item.stn_qty) || 0;
                      const value = price * qty;
                      return (
                        <tr key={`${item.stn_itemcode}-${index}`}>
                          <td>{index + 1}</td>
                          <td>{item.stn_itemcode}</td>
                          <td>{item.stn_desc}</td>
                          <td>{item.stn_uom}</td>
                          <td>{item.itemmaster_closingStock}</td>
                          <td>{price}</td>
                          <td>
                            <input name="qty" type="text" value={qty} readOnly />
                          </td>
                          <td>{value.toFixed(2)}</td>
                          <td className="del">
                            <i
                              className="fa-solid fa-trash-can"
                              onClick={() =>
                                setSelectedItems((prev) =>
                                  prev.filter((_, i) => i !== index)
                                )
                              }
                            ></i>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="10">No items</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="btn-cls">
              <button type="submit" className="submit-btn">Checkout</button>
            </div>
          </form>
        </div>
      </div>

      {/* STN requisition picker */}
      <SupplierListModal
        showModal={showReqModal}
        closeModal={() => setShowReqModal(false)}
        onSelectVehicle={handleVehicleSelect}   // { stnReqNo, toBranch, dueDate }
        branch={branch}
      />
    </div>
  );
};

export default AddStockTransferNote;
